import React, { useState, useEffect } from "react";
import { Phone, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useLang } from "../../lib/i18n";
import { WHATSAPP_NUMBER, CONTACTS, isSupportOnline } from "../../lib/data";

const WhatsAppGlyph = ({ size = 22 }) => (
  <svg viewBox="0 0 32 32" width={size} height={size} fill="currentColor" aria-hidden="true">
    <path d="M16.004 3.2c-7.06 0-12.8 5.74-12.8 12.8 0 2.257.593 4.46 1.72 6.402L3.2 28.8l6.56-1.71a12.74 12.74 0 006.243 1.62h.005c7.06 0 12.8-5.74 12.8-12.8 0-3.42-1.332-6.635-3.75-9.052A12.71 12.71 0 0016.004 3.2zm0 23.02h-.004a10.6 10.6 0 01-5.4-1.48l-.387-.23-4.01 1.046 1.07-3.905-.252-.4a10.56 10.56 0 01-1.62-5.645c0-5.86 4.77-10.63 10.63-10.63 2.84 0 5.507 1.107 7.514 3.115a10.56 10.56 0 013.113 7.52c0 5.86-4.77 10.63-10.63 10.63zm5.83-7.96c-.32-.16-1.89-.933-2.183-1.04-.293-.107-.507-.16-.72.16-.213.32-.826 1.04-1.013 1.253-.187.213-.373.24-.693.08-.32-.16-1.35-.498-2.57-1.586-.95-.847-1.59-1.893-1.777-2.213-.187-.32-.02-.493.14-.652.144-.143.32-.373.48-.56.16-.187.213-.32.32-.533.107-.213.053-.4-.027-.56-.08-.16-.72-1.734-.986-2.374-.26-.623-.523-.538-.72-.548l-.613-.01c-.213 0-.56.08-.853.4-.293.32-1.12 1.094-1.12 2.667 0 1.573 1.147 3.093 1.307 3.307.16.213 2.253 3.44 5.46 4.827.763.33 1.36.527 1.824.674.767.244 1.464.21 2.016.127.615-.092 1.89-.773 2.157-1.52.267-.747.267-1.387.187-1.52-.08-.133-.293-.213-.613-.373z" />
  </svg>
);

// Floating WhatsApp live-chat widget with team online-hours status.
export const FloatingWhatsApp = () => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [online, setOnline] = useState(isSupportOnline());
  useEffect(() => {
    const id = setInterval(() => setOnline(isSupportOnline()), 60000);
    return () => clearInterval(id);
  }, []);
  const callNumber = CONTACTS[0].replace(/\s+/g, "");
  const waUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(t.whatsapp.greeting)}`;

  return (
    <div className="fixed bottom-24 right-6 z-[90] flex flex-col items-end gap-3" data-testid="floating-contact">
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.96 }}
            transition={{ duration: 0.2 }}
            className="w-[320px] max-w-[calc(100vw-3rem)] overflow-hidden rounded-2xl border border-white/10 bg-[#141414] shadow-2xl"
            data-testid="chat-widget"
          >
            {/* Header */}
            <div className="bg-[#075E54] px-5 py-4 flex items-center gap-3">
              <span className="grid place-items-center h-10 w-10 rounded-full bg-[#25D366] text-white">
                <WhatsAppGlyph size={22} />
              </span>
              <div className="flex-1">
                <p className="text-white font-semibold text-sm">{t.whatsapp.title}</p>
                <p className="flex items-center gap-1.5 text-[11px] text-white/80" data-testid="chat-status">
                  <span className={`inline-block h-2 w-2 rounded-full ${online ? "bg-lime2" : "bg-amber-400"}`} />
                  {online ? t.whatsapp.online : t.whatsapp.offline}
                </p>
              </div>
              <button onClick={() => setOpen(false)} data-testid="chat-close" aria-label="Close" className="text-white/70 hover:text-white">
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <div className="px-5 py-5 space-y-3">
              <div className="rounded-xl rounded-tl-sm bg-white/5 px-4 py-3 text-sm text-white/80">
                {online ? t.whatsapp.replyOnline : t.whatsapp.replyOffline}
              </div>
              <p className="text-[11px] text-white/40">{t.whatsapp.hours}</p>

              <a
                href={waUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="floating-whatsapp"
                className="flex items-center justify-center gap-2 rounded-full bg-[#25D366] text-white font-semibold py-3 hover:brightness-105 transition"
              >
                <WhatsAppGlyph size={18} /> {t.whatsapp.start}
              </a>
              <a
                href={`tel:${callNumber}`}
                data-testid="floating-call"
                className="flex items-center justify-center gap-2 rounded-full border border-white/15 text-white/80 font-medium py-2.5 hover:border-lime2 hover:text-lime2 transition"
              >
                <Phone size={16} /> {t.whatsapp.call}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="floating-contact-toggle"
        aria-label={open ? "Close chat" : t.whatsapp.label}
        aria-expanded={open}
        className="relative grid place-items-center h-14 w-14 rounded-full bg-[#25D366] text-white shadow-[0_10px_30px_rgba(37,211,102,0.45)] hover:brightness-110 transition"
      >
        {!open && <span className="absolute inset-0 rounded-full bg-[#25D366] animate-ping opacity-20" />}
        <span className="relative z-10">{open ? <X size={24} /> : <WhatsAppGlyph size={26} />}</span>
        {!open && online && (
          <span className="absolute top-0 right-0 h-3.5 w-3.5 rounded-full bg-lime2 border-2 border-[#141414]" />
        )}
      </button>
    </div>
  );
};
